// src/pages/Payment.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  apiCreateOrder, apiGetPurchaseHistory,
  apiDownloadInvoice, apiDownloadReceipt, apiGetProfile,
} from '../services/api';
import styles from './Payment.module.css';

const PRICE_PER_BUNDLE   = 599;
const CREDITS_PER_BUNDLE = 200;
const QUICK_PICKS        = [1, 3, 5];

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export default function Payment() {
  const navigate = useNavigate();

  const [quantity, setQuantity]   = useState(1);
  const [customMode, setCustomMode] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [missingFields, setMissingFields] = useState(null);

  const [purchases, setPurchases] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    const data = await apiGetPurchaseHistory();
    setPurchases(data.purchases || []);
    setHistoryLoading(false);
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { apiGetProfile().then(setProfile); }, []);

  const handlePay = async () => {
    setLoading(true);
    setError(null);
    setMissingFields(null);
    try {
      const order = await apiCreateOrder(quantity);
      openRazorpay(order);
    } catch (err) {
      if (err.missing_fields) {
        setMissingFields(err.missing_fields);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const openRazorpay = (order) => {
    const rzp = new window.Razorpay({
      key: order.razorpay_key_id,
      amount: order.amount * 100,
      currency: 'INR',
      order_id: order.razorpay_order_id,
      name: 'Delefy',
      description: `${quantity} Plus Bundle(s)`,
      prefill: {
        name: profile?.full_name || '',
        email: profile?.email || '',
        contact: profile?.phone || '',
      },
      handler: () => {
        // Stay on this page — just refresh the history below.
        // Webhook may take a moment; a couple retries covers that gap.
        loadHistory();
        setTimeout(loadHistory, 2000);
        setTimeout(loadHistory, 5000);
      },
      theme: { color: '#1A1A1A' },
    });
    rzp.open();
  };

  const handleDownload = async (receiptId, type) => {
    try {
      const blob = type === 'receipt' ? await apiDownloadReceipt(receiptId) : await apiDownloadInvoice(receiptId);
      downloadBlob(blob, `${type}_${receiptId}.pdf`);
    } catch (err) {
      alert(err.message);
    }
  };

  const selectQuickPick = (n) => {
    setCustomMode(false);
    setQuantity(n);
  };

  const totalAmount  = PRICE_PER_BUNDLE * quantity;
  const totalCredits = CREDITS_PER_BUNDLE * quantity;

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <button className={styles.logoBtn} onClick={() => navigate('/')} type="button">
          <span className={styles.logoBtnIcon}>D</span>
          <span className={styles.logoBtnText}>Delefy <span className={styles.logoBtnVersion}>v1.0</span></span>
        </button>
        <button className={styles.navBtn} onClick={() => navigate('/')} type="button">
          Back to Dashboard
        </button>
      </header>

      <main className={styles.main}>

        {/* ── Buy Section ── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Purchase Bill Credits</h2>
            <p className={styles.sectionDesc}>
              ₹{PRICE_PER_BUNDLE} per bundle — {CREDITS_PER_BUNDLE} bill credits each. Credits never expire through use.
            </p>
          </div>

          <div className={styles.buyCard}>
            <div className={styles.quickPicks}>
              {QUICK_PICKS.map(n => (
                <button
                  key={n}
                  type="button"
                  className={`${styles.pickChip} ${!customMode && quantity === n ? styles.pickChipActive : ''}`}
                  onClick={() => selectQuickPick(n)}
                >
                  <span className={styles.pickChipQty}>{n}</span>
                  <span className={styles.pickChipLabel}>bundle{n > 1 ? 's' : ''}</span>
                  <span className={styles.pickChipCredits}>{n * CREDITS_PER_BUNDLE} credits</span>
                </button>
              ))}
              <button
                type="button"
                className={`${styles.pickChip} ${customMode ? styles.pickChipActive : ''}`}
                onClick={() => setCustomMode(true)}
              >
                <span className={styles.pickChipQty}>Custom</span>
                <span className={styles.pickChipLabel}>choose amount</span>
              </button>
            </div>

            {customMode && (
              <div className={styles.customRow}>
                <label className={styles.fieldLabel}>Number of bundles</label>
                <input
                  type="number" min="1" max="100" value={quantity}
                  className={styles.customInput}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
            )}

            <div className={styles.orderSummary}>
              <div className={styles.summaryRow}>
                <span>{quantity} bundle{quantity > 1 ? 's' : ''} × ₹{PRICE_PER_BUNDLE}</span>
                <span>₹{totalAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Bill credits</span>
                <span>{totalCredits.toLocaleString('en-IN')}</span>
              </div>
              <div className={styles.summaryDivider} />
              <div className={styles.summaryRowTotal}>
                <span>Total</span>
                <span>₹{totalAmount.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {missingFields && (
              <div className={styles.warningBanner}>
                <span>Please complete your profile before purchasing — missing: {missingFields.join(', ').replace(/_/g, ' ')}.</span>
                <button
                  className={styles.warningLink}
                  onClick={() => navigate('/profile', { state: { returnTo: '/payment' } })}
                  type="button"
                >
                  Complete Profile
                </button>
              </div>
            )}

            {error && <div className={styles.errorBanner}>{error}</div>}

            <button className={styles.payBtn} onClick={handlePay} disabled={loading} type="button">
              {loading ? <span className={styles.spinnerSm} /> : null}
              {loading ? 'Processing...' : `Pay ₹${totalAmount.toLocaleString('en-IN')}`}
            </button>
          </div>
        </div>

        {/* ── Purchase History ── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Purchase History</h2>
            <p className={styles.sectionDesc}>Download invoices and receipts for your records.</p>
          </div>

          {historyLoading ? (
            <div className={styles.loadingWrap}>
              <span className={styles.spinnerDark} />
              Loading history...
            </div>
          ) : purchases.length === 0 ? (
            <div className={styles.emptyCard}>No purchases yet.</div>
          ) : (
            <div className={styles.historyList}>
              {purchases.map((p) => (
                <div key={p.receipt_id} className={styles.historyRow}>
                  <div className={styles.historyInfo}>
                    <span className={styles.historyMain}>
                      {p.bundles_quantity} bundle{p.bundles_quantity > 1 ? 's' : ''} · {p.credits_purchased} credits
                    </span>
                    <span className={styles.historyMeta}>
                      ₹{Number(p.amount_paid).toLocaleString('en-IN')} · {new Date(p.paid_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <div className={styles.historyActions}>
                    <button className={styles.historyBtn} onClick={() => handleDownload(p.receipt_id, 'receipt')} type="button">
                      Receipt
                    </button>
                    <button className={styles.historyBtn} onClick={() => handleDownload(p.receipt_id, 'invoice')} type="button">
                      Invoice
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </main>
    </div>
  );
}